import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"

/**
 * Problem browser modal controller for the ticket sidebar "Link problem"
 * action. Mirrors the merge_ticket_controller pattern: a centered modal whose
 * body is fetched server-side from /support/problems/browse so the markup,
 * filters and pagination stay in sync with the standalone problems index.
 *
 * The sidebar trigger opens a choice modal first, the problem twin of step 0
 * in the Link Jira modal: "Create" is a plain link to the new-problem page
 * (pre-linked via ticket_id), "Link" hands off to the browser modal.
 *
 * data-controller="support--problem-browser"
 * data-support--problem-browser-browse-url-value="/.../support/problems/browse"
 * data-support--problem-browser-link-url-value="/.../support/tickets/:id/link_problem"
 * data-support--problem-browser-ticket-id-value="uuid"
 */
export default class extends Controller {
  static targets = [
    "choiceModal",
    "createChoiceBtn",
    "modal",
    "body",
    "placeholder",
    "content",
    "searchInput",
  ]

  static values = {
    browseUrl: String,
    linkUrl: String,
    ticketId: String,
  }

  connect() {
    this._searchTimeout = null
    this._params = {}
  }

  openChoice(event) {
    event?.preventDefault()
    this.choiceModalTarget.classList.remove("hidden")
    requestAnimationFrame(() => this.createChoiceBtnTarget.focus())
  }

  chooseLink(event) {
    this.closeChoice(event)
    this.open()
  }

  closeChoice(event) {
    event?.preventDefault()
    this.choiceModalTarget.classList.add("hidden")
  }

  open(event) {
    event?.preventDefault()
    this.modalTarget.classList.remove("hidden")
    this._params = { page: 1, ticket_id: this.ticketIdValue }
    this._loadBody().then(() => {
      const input = this._scopedQuery(
        '[data-support--problem-browser-target="searchInput"]',
      )
      if (input) input.focus()
    })
  }

  close(event) {
    event?.preventDefault()
    this.modalTarget.classList.add("hidden")
  }

  async _loadBody() {
    try {
      const url = `${this.browseUrlValue}?${new URLSearchParams(this._cleanParams()).toString()}`
      const response = await get(url, { responseKind: "html" })
      if (!response.ok) throw new Error("Browse failed")
      const html = await response.html
      this.bodyTarget.innerHTML = html
    } catch {
      this.bodyTarget.innerHTML = `
        <div class="flex items-center justify-center py-16 text-xs text-muted-foreground">
          Could not load problems. Try closing and reopening the dialog.
        </div>`
    }
  }

  search(event) {
    const value = event.target.value
    if (this._searchTimeout) clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => {
      this._params.q = value
      this._params.page = 1
      this._loadBody().then(() => this._restoreSearchFocus(value))
    }, 300)
  }

  setStatus(event) {
    const status = event.currentTarget.dataset.status
    this._params.status = status
    this._params.page = 1
    this._loadBody()
  }

  setFilter(event) {
    const key = event.currentTarget.dataset.filter
    this._params[key] = event.currentTarget.value
    this._params.page = 1
    this._loadBody()
  }

  prevPage() {
    this._params.page = Math.max(1, (parseInt(this._params.page, 10) || 1) - 1)
    this._loadBody()
  }

  nextPage() {
    this._params.page = (parseInt(this._params.page, 10) || 1) + 1
    this._loadBody()
  }

  async select(event) {
    const row = event.currentTarget
    const problemId = row.dataset.problemId
    if (!problemId) return

    row.classList.add("opacity-60", "pointer-events-none")
    const fd = new FormData()
    fd.append("problem_id", problemId)
    try {
      const response = await post(this.linkUrlValue, {
        body: fd,
        responseKind: "turbo-stream",
      })
      if (response.ok) {
        this.close()
      } else {
        row.classList.remove("opacity-60", "pointer-events-none")
      }
    } catch {
      row.classList.remove("opacity-60", "pointer-events-none")
    }
  }

  _cleanParams() {
    return Object.fromEntries(
      Object.entries(this._params).filter(
        ([, v]) => v !== undefined && v !== null && v !== "",
      ),
    )
  }

  _scopedQuery(selector) {
    return this.bodyTarget.querySelector(selector)
  }

  _restoreSearchFocus(value) {
    const input = this._scopedQuery(
      '[data-support--problem-browser-target="searchInput"]',
    )
    if (input) {
      input.focus()
      // Caret at end so the user can keep typing
      const len = (value || "").length
      try {
        input.setSelectionRange(len, len)
      } catch {
        /* selection unsupported on some inputs */
      }
    }
  }
}
